import axios, { AxiosInstance } from 'axios'
import { User, LoginRequest, RegisterRequest, AuthResponse, UpdateProfileRequest } from '@/types/user'

interface BackendLoginResponse {
  success: boolean;
  user?: User;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
  errors?: Array<{
    field: string;
    message: string;
    code: string;
  }>;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<BackendLoginResponse>('/auth/login', credentials)
    const data = response.data
    
    if (!data.success || !data.user || !data.tokens) {
      throw new Error(data.errors?.[0]?.message || 'Login failed')
    }
    
    return {
      user: data.user,
      token: data.tokens.accessToken,
      refreshToken: data.tokens.refreshToken
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.client.post<BackendLoginResponse>('/auth/register', userData)
    const data = response.data
    
    if (!data.success || !data.user || !data.tokens) {
      throw new Error(data.errors?.[0]?.message || 'Registration failed')
    }
    
    return {
      user: data.user,
      token: data.tokens.accessToken,
      refreshToken: data.tokens.refreshToken
    }
  }

  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    // Add token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await this.client.post<BackendLoginResponse>('/auth/refresh', {
      refreshToken: refreshToken
    })

    if (!response.data.success || !response.data.user || !response.data.tokens) {
      throw new Error('Token refresh failed')
    }

    return {
      user: response.data.user,
      token: response.data.tokens.accessToken,
      refreshToken: response.data.tokens.refreshToken
    }
  }

  async verifyToken(token: string): Promise<User> {
    const response = await this.client.get('/auth/verify', {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/auth/me')
    return response.data
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await this.client.put('/users/profile', data)
    return response.data
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.client.post('/auth/forgot-password', { email })
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.client.post('/auth/reset-password', { token, newPassword: password })
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password', {
      currentPassword,
      newPassword
    })
  }
}

export const authService = new AuthService()